import React from 'react';
import { ImagePreview } from '@/components';
import { Handover, getBusinessUnitLabel } from '@/types/common';
import HandoverIdField from './HandoverIdField';

/**
 * Which leg of the row to render. One handover row holds BOTH legs — the unit
 * going out and coming back — so a screen that opened it "as the return" was
 * still shown everything the pickup recorded, and vice versa.
 *
 * Undefined means the whole row, which is what the standalone handover screens
 * want: there is no single leg being asked about there.
 */
export type HandoverLeg = 'HANDOVER' | 'RETURN';

interface HandoverDetailContentProps {
  handover: Handover;
  titlePlate: string;
  returnReasonValue: string;
  returnReasonDetail: string;
  conditionValue: string;
  equipmentChecklist: string[];
  formatOdometer: (value?: number) => string;
  formatDateTime: (value?: string) => string;
  /** A planned date plus its window, when the leg has one. */
  formatSchedule: (
    value?: string | null,
    slot?: { startTime?: string | null; endTime?: string | null }
  ) => string;
  displayValue: (value?: string | number | null) => string | number;
  leg?: HandoverLeg;
}

const StatusIcon: React.FC<{ checked?: boolean }> = ({ checked = true }) => (
  <span
    className={`inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px] ${
      checked
        ? 'border-green-500 text-green-600'
        : 'border-red-500 text-red-500'
    }`}
  >
    {checked ? '✓' : 'X'}
  </span>
);

const Band: React.FC<{ children: React.ReactNode; inset?: string }> = ({
  children,
  inset = 'px-4',
}) => (
  <div
    className={`bg-gray-50 ${inset} py-2 text-[11px] font-medium text-gray-400 tracking-wider`}
  >
    {children}
  </div>
);

const Cell: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div className="text-[12px] text-gray-400">{label}</div>
    <div className="font-medium text-gray-900">{children}</div>
  </div>
);

/** BAST links are stored bare as often as absolute. */
export const hrefFor = (link?: string | null): string =>
  !link ? '' : link.startsWith('http') ? link : `https://${link}`;

const DocumentLink: React.FC<{ link?: string | null }> = ({ link }) =>
  link ? (
    <a
      href={hrefFor(link)}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 text-purple-600 hover:underline"
    >
      <i className="ri-file-pdf-2-line text-base" />
      View document
    </a>
  ) : (
    <>-</>
  );

const Photo: React.FC<{ src?: string | null; alt: string }> = ({ src, alt }) =>
  src ? (
    <ImagePreview
      src={src}
      alt={alt}
      className="h-48 w-full rounded-md object-contain"
    />
  ) : (
    <div className="flex h-48 items-center justify-center rounded-md bg-gray-50 text-sm text-gray-400">
      No image available
    </div>
  );

const HandoverDetailContent: React.FC<HandoverDetailContentProps> = ({
  handover,
  titlePlate,
  returnReasonValue,
  returnReasonDetail,
  conditionValue,
  equipmentChecklist,
  formatOdometer,
  formatDateTime,
  formatSchedule,
  displayValue,
  leg,
}) => {
  // Identity, the unit and the mitra belong to the row, so they show on either
  // leg. Everything with a `return*` column behind it belongs to one.
  const showHandover = leg !== 'RETURN';
  const showReturn = leg !== 'HANDOVER';

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-6">
        <div className="border border-gray-200 rounded-lg shadow-sm">
          <div className="border-b border-gray-100 px-5 py-3 text-sm font-medium text-gray-700">
            Vehicle Details
          </div>
          <div className=" py-4 text-sm text-gray-700">
            <div className="space-y-4">
              {/* First, because it identifies the record every other band
                  describes — and because it is what ops paste into a ticket. */}
              <div>
                <Band>HANDOVER</Band>
                <div className="px-4 py-3">
                  <HandoverIdField id={handover.ID} />
                </div>
              </div>

              <div>
                <Band>VEHICLE</Band>
                <div className="px-4 py-3 space-y-3">
                  <Cell label="Plate Number">{displayValue(titlePlate)}</Cell>
                  <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-3">
                    <Cell label="OEM">{displayValue(handover.oemName)}</Cell>
                    <Cell label="Models">
                      {displayValue(handover.modelName)}
                    </Cell>
                  </div>
                </div>
              </div>

              <div>
                <Band>HUB LOCATION &amp; BUSINESS UNIT</Band>
                <div className="px-4 py-3 grid grid-cols-2 gap-4">
                  <Cell label="Hub Location">
                    {displayValue((handover as any).vehicleHubName)}
                  </Cell>
                  <Cell label="Business Unit">
                    {getBusinessUnitLabel(
                      (handover as any).vehicleBusinessUnit
                    )}
                  </Cell>
                </div>
              </div>

              <div>
                <Band>MITRA</Band>
                <div className="px-4 py-3 grid grid-cols-2 gap-4">
                  <Cell label="MTRCode">
                    {displayValue(handover.driverCode)}
                  </Cell>
                  <Cell label="Mitra Name">
                    {displayValue(handover.driverName)}
                  </Cell>
                </div>
                <div className="px-4 py-3 grid grid-cols-2 gap-4 border-t border-gray-100">
                  <Cell label="Client">
                    {displayValue(handover.clientName)}
                  </Cell>
                  <Cell label="Pitstop">{displayValue(handover.hubName)}</Cell>
                </div>
              </div>

              {/* The plan for the leg being read. While a unit is waiting to be
                  collected or waiting to come back, this is the only date it
                  has — the actual one below is still null. */}
              <div>
                <Band>SCHEDULE</Band>
                <div className="px-4 py-3 grid grid-cols-2 gap-4">
                  {showHandover && (
                    <Cell label="Scheduled Handover">
                      {formatSchedule(handover.scheduledHandoverDate, {
                        startTime: handover.slotStartTime,
                        endTime: handover.slotEndTime,
                      })}
                    </Cell>
                  )}
                  {showReturn && (
                    <Cell label="Scheduled Return">
                      {/* Date only — the slot window belongs to the pickup. */}
                      {formatSchedule(handover.scheduledReturnDate)}
                    </Cell>
                  )}
                </div>
              </div>

              {showHandover && (
                <>
                  <div>
                    <Band>HANDOVER INFO</Band>
                    <div className="px-4 py-3 grid grid-cols-2 gap-4">
                      <Cell label="Handover Date">
                        {formatDateTime(handover.handoverDate)}
                      </Cell>
                      <Cell label="Odometer at Handover">
                        {formatOdometer(handover.odometer)}
                      </Cell>
                    </div>
                  </div>

                  <div>
                    <Band>HANDOVER DOCUMENTATION</Band>
                    <div className="px-4 py-3 space-y-3">
                      <Cell label="Document BAST">
                        <span className="break-words">
                          <DocumentLink link={handover.bastLink} />
                        </span>
                      </Cell>
                      <div className="border-t border-gray-100 pt-3">
                        <Cell label="Notes">
                          <span className="break-words">
                            {displayValue(handover.notes)}
                          </span>
                        </Cell>
                      </div>
                      <div className="border-t border-gray-100 pt-3">
                        <div className="text-[12px] text-gray-400">
                          Foto Kendaraan
                        </div>
                        <div className="mt-3">
                          <Photo
                            src={handover.vehicleHandoverImageURL}
                            alt="Handover"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {showReturn && (
                <>
                  <div>
                    <Band>RETURN INFO</Band>
                    <div className="px-4 py-3 space-y-3">
                      <Cell label="Return Reason">
                        {returnReasonValue}
                        {returnReasonDetail}
                      </Cell>
                      <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-3">
                        <Cell label="Returned Date">
                          {formatDateTime(handover.returnDate)}
                        </Cell>
                        <Cell label="Odometer at Return">
                          {formatOdometer(handover.returnOdometer)}
                        </Cell>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Band>RETURN DOCUMENTATION</Band>
                    <div className="px-4 py-3 space-y-3">
                      <Cell label="Document BAST">
                        <span className="break-words">
                          <DocumentLink link={handover.returnBastLink} />
                        </span>
                      </Cell>
                      <div className="border-t border-gray-100 pt-3">
                        <Cell label="Notes">
                          <span className="break-words">
                            {displayValue(handover.returnNotes)}
                          </span>
                        </Cell>
                      </div>
                      <div className="border-t border-gray-100 pt-3">
                        <div className="text-[12px] text-gray-400">
                          Foto Mitra &amp; Kendaraan
                        </div>
                        <div className="mt-3">
                          <Photo
                            src={handover.vehicleAndMitraReturnImageURL}
                            alt="Return"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {showHandover && (
          <div className="border border-gray-200 rounded-lg shadow-sm">
            <div className="border-b border-gray-100 px-5 py-3 text-sm font-medium text-gray-700">
              Kelengkapan Unit
            </div>
            {/* What went out with the unit. Every item recorded at handover was
                present by definition — an absent one is not on the list — so
                these carry no cross, unlike the return column beside them. */}
            <div className="px-5 py-4">
              {handover.unitEquipment?.length ? (
                <div className="space-y-3">
                  {handover.unitEquipment.map((item) => (
                    <div
                      key={item}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-700">{item}</span>
                      <StatusIcon checked />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-400">
                  No equipment recorded at handover
                </div>
              )}
            </div>
          </div>
        )}

        {showReturn && (
          <div className="border border-gray-200 rounded-lg shadow-sm">
            <div className="border-b border-gray-100 px-5 py-3 text-sm font-medium text-gray-700">
              Inspection &amp; Checklist
            </div>
            <div className="pb-4 text-sm text-gray-700">
              <Band inset="px-5">VEHICLE CONDITION &amp; FOTO KENDARAAN</Band>
              <div className="px-5 py-4 space-y-4">
                <div>
                  <div className="text-[12px] text-gray-400">
                    Vehicle Condition
                  </div>
                  <div className="mt-1 font-medium text-gray-900">
                    {conditionValue}
                  </div>
                </div>
                <div>
                  <div className="text-[12px] text-gray-400">
                    Foto Kendaraan
                  </div>
                  <div className="mt-3">
                    <Photo src={handover.vehicleReturnImageURL} alt="Return" />
                  </div>
                </div>
              </div>

              <Band inset="px-5">EQUIPMENT CHECKLIST</Band>
              <div className="px-5 py-4">
                {equipmentChecklist.length > 0 ? (
                  <div className="space-y-3">
                    {equipmentChecklist.map((item) => {
                      const isChecked =
                        handover.returnUnitEquipment?.includes(item) ?? false;
                      return (
                        <div
                          key={item}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-gray-700">{item}</span>
                          <StatusIcon checked={isChecked} />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">
                    No equipment checklist available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HandoverDetailContent;
